import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Calendar, ArrowLeft, Loader2 } from "lucide-react";
import { API_URL } from "../utils/api";
import { renderContentHtml } from "../utils/renderContentHtml";

// Header, footer and widget come from <NexusLayout /> — never duplicate them
// here, that's what produced the stale double-header on /blog/:slug.

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadPost = async () => {
      try {
        const response = await fetch(`${API_URL}/api/blog/${slug}`);
        if (!response.ok) throw new Error("Post not found");
        const data = await response.json();
        setPost(data.post);
      } catch (err) {
        setError("Inlägget kunde inte hittas");
      } finally {
        setLoading(false);
      }
    };
    loadPost();
  }, [slug]);

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString("sv-SE", {
      year: "numeric", month: "long", day: "numeric",
    });

  if (loading) {
    return (
      <div className="elido-delayed-public-loader min-h-[60vh] flex items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#255E69]" />
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center bg-gray-50 px-6 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Inlägget hittades inte</h1>
        <Link to="/blog" className="text-[#255E69] hover:underline">
          Tillbaka till bloggen
        </Link>
      </div>
    );
  }

  const pageTitle = post.meta_title || post.title || "Elido Blogg";
  const pageDescription = post.meta_description || post.excerpt || "";

  return (
    <>
      <Helmet
        title={pageTitle}
        meta={[
          { name: "description", content: pageDescription },
          { name: "robots", content: "index,follow" },
        ]}
        link={[{ rel: "canonical", href: `https://elido.nu/blog/${post.slug}` }]}
      />

      <div className="bg-white">
        <article className="py-12 lg:py-16">
          <div className="max-w-3xl mx-auto px-6">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-[#255E69] hover:gap-3 transition-all mb-8"
            >
              <ArrowLeft className="w-4 h-4" />
              Tillbaka till bloggen
            </Link>

            {post.featured_image && (
              <img
                src={post.featured_image}
                alt={post.featured_image_alt || post.title}
                className="w-full rounded-2xl mb-8 shadow-lg"
              />
            )}

            <div className="flex items-center gap-4 text-sm text-gray-500 mb-6">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {formatDate(post.created_at)}
              </div>
              {post.author && <span>Av {post.author}</span>}
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
              {post.title}
            </h1>

            {post.excerpt && (
              <p className="text-xl text-gray-600 mb-8 leading-relaxed">{post.excerpt}</p>
            )}

            <div
              className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-600 prose-a:text-[#255E69]"
              dangerouslySetInnerHTML={{ __html: renderContentHtml(post.content) }}
            />

            <div className="mt-12 p-8 bg-gradient-to-r from-[#255E69] to-[#3A8A98] rounded-2xl text-center">
              <h2 className="text-2xl font-bold text-white mb-2">
                Behöver du hjälp med din hemsida?
              </h2>
              <p className="text-white/80 mb-6">
                Kontakta oss för en kostnadsfri konsultation.
              </p>
              <Link
                to="/#kontakt"
                className="inline-block bg-white text-[#255E69] px-6 py-3 rounded-xl font-semibold hover:bg-gray-100 transition-colors"
              >
                Kontakta oss
              </Link>
            </div>
          </div>
        </article>
      </div>
    </>
  );
}
